// Page chrome — how a screen tells the ONE shell what to put in the header.
//
// On a phone the header is the shell's (PhoneShell renders eyebrow + title);
// on desktop the screen renders its own PageHeader inside the content area.
// Most screens need nothing here: AppShell falls back to the destination's
// label from navigation.js, so /invoices says "Invoices" without being asked.
//
// The exceptions are the detail screens — a job, one person's timesheet —
// where the useful header is the THING, not the section. Before the rebuild
// refresh those screens passed a title into their own shell wrapper; there is
// no per-app shell any more, so they hand it up instead.
//
// Deliberately a title only. Anything richer (actions, tabs) belongs in the
// screen's body where it can be seen at both widths — a control that exists
// only in the phone header is a control the desktop user cannot reach, which
// is the class of bug docs/ONE_APP.md exists to end.

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const ChromeContext = createContext(null);

export function ChromeProvider({ children }) {
  const [chrome, setChrome] = useState(null);
  const value = useMemo(() => ({ chrome, setChrome }), [chrome]);
  return <ChromeContext.Provider value={value}>{children}</ChromeContext.Provider>;
}

/** What the shell should show. `null` means "use the destination's label". */
export function useChrome() {
  const ctx = useContext(ChromeContext);
  return (ctx && ctx.chrome) || null;
}

/**
 * Set the phone header for as long as this screen is mounted.
 *
 * Clears on unmount, so a stale title can never outlive the screen that set
 * it — React runs every cleanup before any effect of the next commit, so a
 * route change sets the new title after the old one has cleared, not before.
 */
export function usePageChrome(title, eyebrow) {
  const ctx = useContext(ChromeContext);
  const set = ctx && ctx.setChrome;
  useEffect(() => {
    if (!set) return undefined;
    set({ title: title || '', eyebrow: eyebrow || '' });
    return () => set(null);
  }, [set, title, eyebrow]);
}

export default ChromeProvider;
